/**
 * #1114 — throttle for the "WhatsApp not connected" messagelog rows.
 *
 * While the page is down every scheduler wake (~3s apart under load) writes one row per pending message:
 * ~1200 rows/hour/message into our biggest collection. Write the row when the message's connection signature
 * (state/stream/hasSynced) changes, or again once `intervalMs` has passed with it unchanged. The send is
 * deferred either way; only the log write is skipped.
 */

export const NOT_CONNECTED_LOG_INTERVAL_MS = 10 * 60_000;
const MAX_TRACKED_MESSAGES = 2_000;

export interface NotConnectedSignature {
    state?: string | null;
    stream?: string | null;
    hasSynced?: boolean | null;
}

export class NotConnectedLogThrottle {
    private readonly last = new Map<string, { signature: string; at: number }>();

    constructor(private readonly cfg: { intervalMs?: number; now?: () => number } = {}) {}

    private get intervalMs(): number { return this.cfg.intervalMs ?? NOT_CONNECTED_LOG_INTERVAL_MS; }
    private now(): number { return (this.cfg.now ?? Date.now)(); }

    /** True when this message should get a not-connected row now (and records that it did). */
    shouldLog(messageId: string, sig: NotConnectedSignature): boolean {
        const signature = `${sig.state ?? ''}|${sig.stream ?? ''}|${sig.hasSynced ?? ''}`;
        const now = this.now();
        const prev = this.last.get(messageId);
        if (prev && prev.signature === signature && now - prev.at < this.intervalMs) return false;
        this.last.delete(messageId);
        this.last.set(messageId, { signature, at: now });
        if (this.last.size > MAX_TRACKED_MESSAGES) {
            const oldest = this.last.keys().next().value;
            if (oldest !== undefined) this.last.delete(oldest);
        }
        return true;
    }

    /** The message got past the gate: the next outage logs afresh. */
    clear(messageId: string): void { this.last.delete(messageId); }

    reset(): void { this.last.clear(); }
}

/** One per content-script lifetime. */
export const notConnectedLogThrottle = new NotConnectedLogThrottle();
